import { useEffect, useRef, useCallback } from 'react';
import { forceReloadForUpdate } from '@/lib/force-reload';

interface VersionInfo {
  version: string;
  buildTime: string;
  critical?: boolean;
}

interface UseVersionCheckOptions {
  interval?: number;
  onUpdateAvailable?: (newVersion: string) => void;
  onCriticalUpdate?: (reload: () => Promise<void>, newVersion: string) => void;
}

export function useVersionCheck(options: UseVersionCheckOptions = {}) {
  const { interval = 5 * 60 * 1000, onUpdateAvailable, onCriticalUpdate } = options;
  const currentVersion = __APP_VERSION__;
  const pendingUpdateRef = useRef<{ critical: boolean; version: string } | null>(null);

  // forceReloadForUpdate concurrently clears caches and unregisters the worker
  // under a time-box, then hard-navigates — so a critical reload can't hang on a
  // busy service worker the way a bare caches-clear-then-reload could.
  const performReload = useCallback(() => forceReloadForUpdate(), []);

  const notifyUpdate = useCallback(
    (critical: boolean, newVersion: string) => {
      if (critical && onCriticalUpdate) {
        onCriticalUpdate(performReload, newVersion);
      } else if (critical) {
        performReload();
      } else if (onUpdateAvailable) {
        onUpdateAvailable(newVersion);
      }
    },
    [onCriticalUpdate, onUpdateAvailable, performReload]
  );

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        return;
      }

      const data: VersionInfo = await response.json();

      if (data.version === currentVersion) {
        return;
      }

      if (pendingUpdateRef.current?.version === data.version) {
        return;
      }

      pendingUpdateRef.current = {
        critical: data.critical ?? false,
        version: data.version,
      };

      notifyUpdate(data.critical ?? false, data.version);
    } catch {
      // Fail silently
    }
  }, [currentVersion, notifyUpdate]);

  useEffect(() => {
    const initialTimeout = setTimeout(checkVersion, 10000);
    const intervalId = setInterval(checkVersion, interval);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (pendingUpdateRef.current?.critical) {
          notifyUpdate(true, pendingUpdateRef.current.version);
        } else {
          checkVersion();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion, interval, notifyUpdate]);
}
